import { useMemo } from 'react';

import { useGmailStatus } from '@/hooks/integrations/google/useGmailStatus';
import { useOutlookStatus } from '@/hooks/integrations/microsoft/useOutlookStatus';
import { useEnsuredStoreObjects } from '@/lib/crm/hooks/useEnsuredStoreObjects';
import type { CrmObject } from '@/types/crm';

/** One user-authored reusable email snippet. */
export interface CannedResponse {
  id: string;
  name: string;
  html: string;
}

/** A snippet plus the integration entity it is stored on. */
export interface CannedResponseWithSource extends CannedResponse {
  entityId: string;
}

export interface CannedResponsesData {
  /** Union of responses across all the user's email integration entities. */
  responses: CannedResponseWithSource[];
  /** Entity id new responses are written to (the user's primary mailbox). */
  primaryEntityId: string | null;
  /** Loaded integration objects by entity id (required by useUpdateCrmProperty). */
  objectsById: Record<string, CrmObject>;
  isLoading: boolean;
}

export const CANNED_RESPONSES_PROPERTY = 'ahoy_canned_responses' as const;

function parseResponses(value: unknown): CannedResponse[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (entry): entry is CannedResponse =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as CannedResponse).id === 'string' &&
      typeof (entry as CannedResponse).name === 'string' &&
      typeof (entry as CannedResponse).html === 'string'
  );
}

/**
 * Read the union of canned responses across the current user's email
 * integration entities (Gmail + Outlook), and identify the primary entity
 * that new responses are written to.
 *
 * Write-to-primary / read-the-union: responses stay visible if the user's
 * primary mailbox changes. Primary is per-provider (each provider flags one
 * account is_primary), so the pick order is: Gmail primary, else Outlook
 * primary, else first Gmail account, else first Outlook account.
 */
export function useCannedResponses(
  orgId: number | undefined
): CannedResponsesData {
  const orgIdStr = orgId !== undefined ? String(orgId) : undefined;
  const { data: gmailStatus, isLoading: gmailLoading } =
    useGmailStatus(orgIdStr);
  const { data: outlookStatus, isLoading: outlookLoading } =
    useOutlookStatus(orgIdStr);

  const { entityIds, primaryEntityId } = useMemo(() => {
    const gmailAccounts = gmailStatus?.accounts ?? [];
    const outlookAccounts = outlookStatus?.accounts ?? [];
    const gmailIds = gmailAccounts
      .map(a => a.gmail_entity_id)
      .filter((id): id is string => Boolean(id));
    const outlookIds = outlookAccounts
      .map(a => a.outlook_mail_entity_id)
      .filter((id): id is string => Boolean(id));
    const primary =
      gmailAccounts.find(a => a.is_primary && a.gmail_entity_id)
        ?.gmail_entity_id ??
      outlookAccounts.find(a => a.is_primary && a.outlook_mail_entity_id)
        ?.outlook_mail_entity_id ??
      gmailIds[0] ??
      outlookIds[0] ??
      null;
    return {
      entityIds: [...gmailIds, ...outlookIds],
      primaryEntityId: primary,
    };
  }, [gmailStatus, outlookStatus]);

  // Gate eager hydration on a valid org. `useEnsuredStoreObjects` fires no
  // fetch when the org is missing/invalid, but with `ensure` defaulting to
  // true it still reports every not-yet-hydrated id as loading — leaving
  // `objectsLoading` (and thus `CannedResponsesData.isLoading`) stuck true
  // forever. Passing `ensure: false` for an invalid org restores the
  // disabled-query behaviour: read what the store has, report not-loading.
  //
  // We read the union of responses from `objects` (not a separate
  // `useStoreObjectsMap`) because `useEnsuredStoreObjects` only resolves an id
  // to a *complete* store entry. A summary/WS-only entry stays unresolved
  // (`object === null`, `loading` still true), so the modal never renders or
  // saves over an integration object whose `ahoy_canned_responses` property
  // hasn't been fully hydrated yet.
  const { objects, isLoading: objectsLoading } = useEnsuredStoreObjects(
    orgId,
    entityIds,
    { ensure: orgId !== undefined && orgId > 0 }
  );

  return useMemo(() => {
    const objectsById: Record<string, CrmObject> = {};
    const responses: CannedResponseWithSource[] = [];
    for (const { entityId, object } of objects) {
      if (!object) {
        continue;
      }
      objectsById[entityId] = object;
      const raw = object.properties?.[CANNED_RESPONSES_PROPERTY]?.value;
      for (const response of parseResponses(raw)) {
        responses.push({ ...response, entityId });
      }
    }
    return {
      responses,
      primaryEntityId,
      objectsById,
      isLoading: gmailLoading || outlookLoading || objectsLoading,
    };
  }, [objects, primaryEntityId, gmailLoading, outlookLoading, objectsLoading]);
}
